import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  Avatar,
  TextField,
  InputAdornment,
} from '@mui/material';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { palette } from '../../theme/colors';

interface HeaderProps {
  title?: string;
  showSearch?: boolean;
}

export const Header: React.FC<HeaderProps> = ({ title, showSearch = true }) => {
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuthStore();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [searchValue, setSearchValue] = useState('');

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    await logout();
    handleMenuClose();
    navigate('/');
  };

  const handleSearch = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && searchValue.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchValue.trim())}`);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        py: 2,
        px: 3,
        backgroundColor: palette.surface,
        borderBottom: `1px solid ${palette.border}`,
        position: 'sticky',
        top: 0,
        zIndex: 1100,
      }}
    >
      {/* Left Section - Title */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
        {title && (
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              color: palette.textPrimary,
            }}
          >
            {title}
          </Typography>
        )}
      </Box>

      {/* Center Section - Search (optional) */}
      {showSearch && (
        <Box sx={{ flex: 1, maxWidth: 400, mx: 4 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search products, stores..."
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onKeyDown={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlinedIcon sx={{ color: palette.textSecondary, fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: palette.sky,
                borderRadius: '50px',
                '& fieldset': {
                  border: 'none',
                },
                '&:hover fieldset': {
                  border: 'none',
                },
                '&.Mui-focused fieldset': {
                  border: `2px solid ${palette.primary}`,
                },
              },
            }}
          />
        </Box>
      )}

      {/* Right Section - Actions & User */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {/* Notification Bell */}
        <IconButton
          sx={{
            width: 40,
            height: 40,
            backgroundColor: palette.skyLight,
            '&:hover': { backgroundColor: palette.sky },
          }}
        >
          <NotificationsNoneOutlinedIcon sx={{ fontSize: 20, color: palette.textPrimary }} />
        </IconButton>

        {/* Settings */}
        <IconButton
          onClick={() => navigate('/account-settings')}
          sx={{
            width: 40,
            height: 40,
            backgroundColor: palette.skyLight,
            '&:hover': { backgroundColor: palette.sky },
          }}
        >
          <SettingsOutlinedIcon sx={{ fontSize: 20, color: palette.textPrimary }} />
        </IconButton>

        {isAuthenticated && user ? (
          <>
            {/* User Menu */}
            <Box
              onClick={handleMenuOpen}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                cursor: 'pointer',
                p: 0.5,
                borderRadius: '50px',
                '&:hover': { backgroundColor: palette.skyLight },
              }}
            >
              <Avatar
                sx={{
                  width: 36,
                  height: 36,
                  bgcolor: palette.primary,
                  color: palette.textOnPrimary,
                  fontWeight: 600,
                  fontSize: '0.9rem',
                }}
              >
                {user.firstName?.[0] || 'U'}
              </Avatar>
              <KeyboardArrowDownIcon sx={{ color: palette.textSecondary, fontSize: 20 }} />
            </Box>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              PaperProps={{
                sx: {
                  mt: 1,
                  borderRadius: 2,
                  boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.12)',
                  minWidth: 200,
                },
              }}
            >
              <Box sx={{ px: 2, py: 1.5 }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  {user.firstName} {user.lastName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {user.email}
                </Typography>
              </Box>
              <Divider />
              <MenuItem
                onClick={() => {
                  navigate('/account-settings');
                  handleMenuClose();
                }}
                sx={{ py: 1.5 }}
              >
                Account Settings
              </MenuItem>
              <MenuItem
                onClick={() => {
                  navigate('/my-reservations');
                  handleMenuClose();
                }}
                sx={{ py: 1.5 }}
              >
                My Reservations
              </MenuItem>
              <MenuItem
                onClick={() => {
                  navigate('/account/b2b-access');
                  handleMenuClose();
                }}
                sx={{ py: 1.5 }}
              >
                B2B Access
              </MenuItem>
              <Divider />
              <MenuItem
                onClick={handleLogout}
                sx={{ py: 1.5, color: palette.error }}
              >
                Logout
              </MenuItem>
            </Menu>
          </>
        ) : (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/login')}
              sx={{
                borderColor: palette.border,
                color: palette.textPrimary,
                '&:hover': {
                  borderColor: palette.primary,
                  backgroundColor: palette.skyLight,
                },
              }}
            >
              Login
            </Button>
            <Button
              variant="contained"
              onClick={() => navigate('/register')}
              sx={{
                bgcolor: palette.primary,
                color: palette.textOnPrimary,
                '&:hover': { bgcolor: palette.primaryDark },
              }}
            >
              Sign Up
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};
